import { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, FormControlLabel, Radio, TextField } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import {
  createCitizenPblCase,
  getCitizenPblCaseDraft,
  getPblCase,
  getPblCitizenContext,
  saveCitizenPblCaseDraft,
  submitCitizenPblCase,
  updateCitizenPblCase,
  uploadPblFile,
} from '@/api/pbl.api';
import { Check } from '@/components/Icon';
import { Button } from '@/components/UI/Button';
import { DropZone } from '@/components/UI/FileDropzone';
import { SignatureModal } from '@/components/UI/Modal';
import { Text } from '@/components/UI/Typography/Text';
import { pblConsultationMethods, pblLegalServiceNeeds } from '@/data/pbl';
import useResize from '@/hooks/useResize';
import PATH from '@/router/path';
import { theme } from '@/theme/Theme';
import type { PblAuthUser, PblConsultationMethod, PblPrimaryLegalServiceNeed, PblServiceArea } from '@/types/pbl';
import { formatPblDisplayDate } from '@/utils/pblDate';
import {
  getPblAssistanceSessionId,
  getPblSubmittedAssistedCaseId,
  isPblAssistanceActive,
  rememberPblSubmittedAssistedCase,
} from '@/utils/pblAssistance';
import { errorHandler, successHandler } from '@/utils/toastHandler';

import { createPblUploadFormData, getUploadedPblFileId } from '../VolunteerRegistration/uploadedDocument';
import { endAssistanceAfterCaseSubmission } from './assistanceCompletion';
import {
  buildCitizenCaseDraftPayload,
  buildCitizenCasePayload,
  buildInitialCitizenCaseForm,
  CITIZEN_CASE_FACTUAL_BACKGROUND_RECOMMENDATION,
  CitizenCaseApplicationForm,
  CitizenCaseUploadedDocument,
  isCitizenCaseApplicationComplete,
  mergeCitizenCaseIntoApplicationForm,
  mergeCitizenContextIntoCaseForm,
  signCitizenCaseApplication,
} from './formState';
import {
  ActionRow,
  DateText,
  DocumentUploadHelp,
  DisclaimerText,
  FieldGroup,
  FormPanel,
  InfoGrid,
  InfoLabel,
  InfoValue,
  OptionStack,
  OptionText,
  PromptText,
  SectionTitle,
  SignaturePreview,
  SignaturePreviewAction,
  SignaturePreviewImage,
  SignatureRow,
  SignedBadge,
  SubmittedAction,
  SubmittedContent,
  SubmittedPanel,
  SubmittedText,
  SubmittedTitle,
  TextFieldStack,
  UploadedDocumentItem,
  UploadedDocumentList,
  UploadedDocumentName,
} from './styles';

type Props = {
  userData?: PblAuthUser;
};

const enquiryCategories: Array<{ value: PblServiceArea; defaultLabel: string; labelKey: string }> = [
  {
    value: 'estate_planning',
    defaultLabel:
      'Estate planning issues (e.g. Three Instruments of Peace: Will, Enduring Powers of Attorney, Advance Medical Directive, etc)',
    labelKey: 'pbl.citizenAuthorizationCaseCreate.caseApplication.enquiryCategory.options.estatePlanning',
  },
  {
    value: 'family_matters',
    defaultLabel: 'Family matters (Divorce, maintenance, custody, Inheritance, etc.)',
    labelKey: 'pbl.citizenAuthorizationCaseCreate.caseApplication.enquiryCategory.options.familyMatters',
  },
  {
    value: 'employment_disputes',
    defaultLabel: 'Employment disputes (wage arrears, unfair dismissal, etc.)',
    labelKey: 'pbl.citizenAuthorizationCaseCreate.caseApplication.enquiryCategory.options.employmentDisputes',
  },
  {
    value: 'building_management',
    defaultLabel: 'Building Management (water seepage, building maintenance, etc.)',
    labelKey: 'pbl.citizenAuthorizationCaseCreate.caseApplication.enquiryCategory.options.buildingManagement',
  },
  {
    value: 'tenancy_and_properties',
    defaultLabel:
      'Tenancy and properties related matters (rental deposit, lease renewal, eviction, purchase / sale of properties etc.)',
    labelKey: 'pbl.citizenAuthorizationCaseCreate.caseApplication.enquiryCategory.options.tenancyAndProperties',
  },
  {
    value: 'criminal_matters',
    defaultLabel: 'Criminal matters',
    labelKey: 'pbl.citizenAuthorizationCaseCreate.caseApplication.enquiryCategory.options.criminalMatters',
  },
];

const dataUrlToFile = (dataUrl: string, filename: string) => {
  const [metadata, base64Data] = dataUrl.split(',');
  const mimeType = metadata.match(/data:(.*);base64/)?.[1] || 'image/png';
  const binary = window.atob(base64Data);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new File([bytes], filename, { type: mimeType });
};

const formatSignatureDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

const CASE_SUPPORTING_DOCUMENT_ALLOWED_EXTENSIONS = new Set(['pdf', 'png', 'jpg', 'jpeg']);
const CASE_SUPPORTING_DOCUMENT_MAX_SIZE_MB = 10;
const CASE_SUPPORTING_DOCUMENT_MAX_SIZE_BYTES = CASE_SUPPORTING_DOCUMENT_MAX_SIZE_MB * 1024 * 1024;
const CASE_SUPPORTING_DOCUMENT_MAX_COUNT = 10;

const getFileExtension = (fileName: string) => fileName.split('.').pop()?.toLowerCase() || '';

const validateCaseSupportingDocuments = (files: File[], tr: (key: string, defaultValue: string) => string) => {
  const hasUnsupportedFileType = files.some(
    (file) => !CASE_SUPPORTING_DOCUMENT_ALLOWED_EXTENSIONS.has(getFileExtension(file.name))
  );

  if (hasUnsupportedFileType) {
    errorHandler(
      tr(
        'pbl.citizenAuthorizationCaseCreate.caseApplication.messages.documentTypeInvalid',
        'Supporting documents must be PDF, PNG, JPG, or JPEG files.'
      )
    );
    return false;
  }

  const hasOversizedFile = files.some((file) => file.size > CASE_SUPPORTING_DOCUMENT_MAX_SIZE_BYTES);

  if (hasOversizedFile) {
    errorHandler(
      tr(
        'pbl.citizenAuthorizationCaseCreate.caseApplication.messages.documentTooLarge',
        'Supporting documents must be 10MB or smaller.'
      )
    );
    return false;
  }

  return true;
};

const CitizenCaseApplication = ({ userData }: Props) => {
  const navigate = useNavigate();
  // @ts-ignore
  const { t } = useTranslation();
  const { caseId } = useParams<{ caseId?: string }>();
  const tr = (key: string, defaultValue: string) => t(key as any, { defaultValue }) as string;
  const { isMobile, isTablet } = useResize();
  const [submitted, setSubmitted] = useState(() => Boolean(getPblSubmittedAssistedCaseId()));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [submittedAssistedCaseId, setSubmittedAssistedCaseId] = useState<number | null>(getPblSubmittedAssistedCaseId);
  const [isEndingAssistance, setIsEndingAssistance] = useState(false);
  const [assistanceEndFailed, setAssistanceEndFailed] = useState(() => Boolean(getPblSubmittedAssistedCaseId()));
  const endingAssistanceRef = useRef(false);
  const [draftCaseId, setDraftCaseId] = useState<number | null>(null);
  const [isUploadingCaseDocument, setIsUploadingCaseDocument] = useState(false);
  const [caseDocumentFiles, setCaseDocumentFiles] = useState<File[]>([]);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [form, setForm] = useState<CitizenCaseApplicationForm>(() => buildInitialCitizenCaseForm(userData));

  const isComplete = useMemo(() => isCitizenCaseApplicationComplete(form), [form]);

  useEffect(() => {
    if (!userData?.pbl_citizen_profile?.password_setup_required) return;

    navigate(`${PATH.pbl.passwordSetup}?redirect_to=${encodeURIComponent(PATH.pbl.citizenCaseCreate)}`, {
      replace: true,
    });
  }, [navigate, userData?.pbl_citizen_profile?.password_setup_required]);

  useEffect(() => {
    if (userData?.pbl_citizen_profile?.password_setup_required) return;
    if (submittedAssistedCaseId) return;

    let isMounted = true;

    const loadCitizenContext = async () => {
      try {
        const response = await getPblCitizenContext();

        if (!isMounted || response.data.success === false) return;

        setForm((prev) => mergeCitizenContextIntoCaseForm(prev, response.data.data));

        const editableCaseResponse = caseId ? await getPblCase(Number(caseId)) : await getCitizenPblCaseDraft();
        const editableCase = editableCaseResponse.data.data;

        if (!isMounted || !editableCase) return;

        setDraftCaseId(editableCase.id);
        setForm((prev) => mergeCitizenCaseIntoApplicationForm(prev, editableCase));
      } catch {
        // The auth payload still provides a usable fallback for older API deployments.
      }
    };

    loadCitizenContext();

    return () => {
      isMounted = false;
    };
  }, [caseId, submittedAssistedCaseId, userData?.pbl_citizen_profile?.password_setup_required]);

  const updateField = <Key extends keyof CitizenCaseApplicationForm>(
    key: Key,
    value: CitizenCaseApplicationForm[Key]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleLegalNeed =
    (value: PblPrimaryLegalServiceNeed) => (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setForm((prev) => ({
        ...prev,
        primary_legal_service_needs: checked
          ? [...prev.primary_legal_service_needs, value]
          : prev.primary_legal_service_needs.filter((item) => item !== value),
        primary_legal_service_needs_other: !checked && value === 'other' ? '' : prev.primary_legal_service_needs_other,
      }));
    };

  const selectConsultationMethod = (value: PblConsultationMethod) => {
    setForm((prev) => ({
      ...prev,
      consultation_methods: [value],
    }));
  };

  useEffect(() => {
    if (caseDocumentFiles.length === 0) return;

    let isMounted = true;

    const uploadCaseDocuments = async () => {
      if (form.uploaded_documents.length + caseDocumentFiles.length > CASE_SUPPORTING_DOCUMENT_MAX_COUNT) {
        errorHandler(
          tr(
            'pbl.citizenAuthorizationCaseCreate.caseApplication.messages.documentCountExceeded',
            'A case may contain at most 10 supporting documents.'
          )
        );
        setCaseDocumentFiles([]);
        return;
      }
      if (!validateCaseSupportingDocuments(caseDocumentFiles, tr)) {
        setCaseDocumentFiles([]);
        return;
      }

      setIsUploadingCaseDocument(true);

      try {
        const uploadedDocuments: CitizenCaseUploadedDocument[] = [];

        for (const file of caseDocumentFiles) {
          const response = await uploadPblFile(createPblUploadFormData(file, 'case_supporting_document'));
          const fileId = getUploadedPblFileId(response.data.data);

          if (response.data.success === false || typeof fileId !== 'number') {
            throw new Error(
              response.data.message ||
                tr(
                  'pbl.citizenAuthorizationCaseCreate.caseApplication.messages.uploadFailed',
                  'Failed to upload supporting document.'
                )
            );
          }

          uploadedDocuments.push({
            storage_file_id: fileId,
            title: file.name,
            document_type: 'case_supporting_document',
            metadata: {
              note: 'uploaded during case creation',
            },
          });
        }

        if (!isMounted || uploadedDocuments.length === 0) return;

        setForm((prev) => ({
          ...prev,
          uploaded_documents: [...prev.uploaded_documents, ...uploadedDocuments],
        }));
        successHandler(
          tr('pbl.citizenAuthorizationCaseCreate.caseApplication.messages.uploaded', 'Supporting document(s) uploaded.')
        );
      } catch (error: any) {
        if (isMounted) {
          errorHandler(
            error.response?.data?.message ||
              error.response?.data?.data ||
              error.message ||
              tr(
                'pbl.citizenAuthorizationCaseCreate.caseApplication.messages.uploadFailed',
                'Failed to upload supporting document.'
              )
          );
        }
      } finally {
        if (isMounted) {
          setIsUploadingCaseDocument(false);
          setCaseDocumentFiles([]);
        }
      }
    };

    uploadCaseDocuments();

    return () => {
      isMounted = false;
    };
  }, [caseDocumentFiles]);

  const closeSignatureModal = () => {
    setIsSignatureModalOpen(false);
  };

  const resetForm = () => {
    setForm(buildInitialCitizenCaseForm(userData));
    closeSignatureModal();
  };

  const saveSignature = async (dataUrl: string) => {
    try {
      const file = dataUrlToFile(dataUrl, 'citizen-case-enquirer-signature.png');
      const response = await uploadPblFile(createPblUploadFormData(file, 'citizen_case_signature'));
      const fileId = getUploadedPblFileId(response.data.data);

      if (response.data.success === false || typeof fileId !== 'number') {
        throw new Error(
          tr('pbl.citizenAuthorizationCaseCreate.shared.messages.signatureUploadFailed', 'Failed to upload signature.')
        );
      }

      setForm((prev) =>
        signCitizenCaseApplication(prev, {
          dataUrl,
          fileId,
          signedAt: formatSignatureDate(new Date()),
        })
      );
      closeSignatureModal();
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message ||
          error.message ||
          tr('pbl.citizenAuthorizationCaseCreate.shared.messages.signatureUploadFailed', 'Failed to upload signature.')
      );
      throw error;
    }
  };

  const saveDraft = async () => {
    if (isSavingDraft || isSubmitting) return;

    setIsSavingDraft(true);

    try {
      const response = draftCaseId
        ? await updateCitizenPblCase(draftCaseId, buildCitizenCaseDraftPayload(form))
        : await saveCitizenPblCaseDraft(buildCitizenCaseDraftPayload(form));

      if (response.data.success === false) {
        errorHandler(
          response.data.message ||
            tr('pbl.citizenAuthorizationCaseCreate.caseApplication.messages.draftSaveFailed', 'Failed to save draft.')
        );
        return;
      }

      setDraftCaseId(response.data.data.id);
      setForm((prev) => mergeCitizenCaseIntoApplicationForm(prev, response.data.data));
      successHandler(
        response.data.message ||
          tr('pbl.citizenAuthorizationCaseCreate.caseApplication.messages.draftSaved', 'Draft saved.')
      );
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message ||
          error.response?.data?.data ||
          tr('pbl.citizenAuthorizationCaseCreate.caseApplication.messages.draftSaveFailed', 'Failed to save draft.')
      );
    } finally {
      setIsSavingDraft(false);
    }
  };

  const finishAssistedSubmission = async (caseId: number) => {
    if (endingAssistanceRef.current) return;
    const sessionId = getPblAssistanceSessionId();
    if (!sessionId) {
      setAssistanceEndFailed(true);
      return;
    }

    endingAssistanceRef.current = true;
    setIsEndingAssistance(true);
    setAssistanceEndFailed(false);
    try {
      await endAssistanceAfterCaseSubmission(sessionId);
      navigate(PATH.pbl.pendingReviewDetail.replace(':caseId', String(caseId)), { replace: true });
    } catch {
      setAssistanceEndFailed(true);
      errorHandler(t('pbl.backlogEnhancements.assistance.endAfterSubmitFailed'));
    } finally {
      endingAssistanceRef.current = false;
      setIsEndingAssistance(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!isComplete || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const response = draftCaseId
        ? await submitCitizenPblCase(draftCaseId, buildCitizenCasePayload(form))
        : await createCitizenPblCase(buildCitizenCasePayload(form));

      if (response.data.success === false) {
        errorHandler(
          response.data.message ||
            tr(
              'pbl.citizenAuthorizationCaseCreate.caseApplication.messages.submitFailed',
              'Failed to submit case enquiry.'
            )
        );
        return;
      }

      successHandler(
        response.data.message ||
          tr('pbl.citizenAuthorizationCaseCreate.caseApplication.messages.submitted', 'Case enquiry submitted.')
      );
      resetForm();
      setDraftCaseId(null);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (isPblAssistanceActive()) {
        rememberPblSubmittedAssistedCase(response.data.data.id);
        setSubmittedAssistedCaseId(response.data.data.id);
        await finishAssistedSubmission(response.data.data.id);
      }
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message ||
          error.response?.data?.data ||
          tr(
            'pbl.citizenAuthorizationCaseCreate.caseApplication.messages.submitFailed',
            'Failed to submit case enquiry.'
          )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayValue = (value: string) => value || '-';

  if (submitted) {
    return (
      <SubmittedPanel isTablet={isTablet}>
        <SubmittedContent>
          <Check.CheckSVG width={80} height={80} fill={theme.colors.secondary400} />
          <SubmittedTitle>
            {tr(
              'pbl.citizenAuthorizationCaseCreate.caseApplication.submitted.title',
              'Application Submitted Successfully'
            )}
          </SubmittedTitle>
          <SubmittedText>
            {tr(
              'pbl.citizenAuthorizationCaseCreate.caseApplication.submitted.reviewNotice',
              'Your case will be sent for review. Please pay attention to the review results notifications we send you.'
            )}
          </SubmittedText>
          <SubmittedText>
            {tr(
              'pbl.citizenAuthorizationCaseCreate.caseApplication.submitted.contactIfNecessary',
              'We will contact you if necessary.'
            )}
          </SubmittedText>
          <SubmittedAction>
            {submittedAssistedCaseId ? (
              <>
                {isEndingAssistance && <SubmittedText role="status">{t('pbl.backlogEnhancements.assistance.endingAfterSubmit')}</SubmittedText>}
                {assistanceEndFailed && (
                  <>
                    <Text role="alert" color={theme.colors.danger}>
                      {t('pbl.backlogEnhancements.assistance.endAfterSubmitFailed')}
                    </Text>
                    <Button
                      type="button"
                      size="lg"
                      variant="primary"
                      rounded={false}
                      disabled={isEndingAssistance}
                      onClick={() => void finishAssistedSubmission(submittedAssistedCaseId)}
                    >
                      {t('pbl.backlogEnhancements.assistance.retryEndAndReview')}
                    </Button>
                  </>
                )}
              </>
            ) : (
              <Button
                type="button"
                size="lg"
                variant="primary"
                rounded={false}
                onClick={() => {
                  setSubmitted(false);
                  navigate('/home');
                }}
              >
                {tr('pbl.citizenAuthorizationCaseCreate.caseApplication.submitted.returnToHome', 'Return to Home')}
              </Button>
            )}
          </SubmittedAction>
        </SubmittedContent>
      </SubmittedPanel>
    );
  }

  return (
    <FormPanel isTablet={isTablet} onSubmit={submit}>
      <SectionTitle>
        {tr(
          'pbl.citizenAuthorizationCaseCreate.shared.sections.districtOfficeStaff',
          'District Office & Staff Information'
        )}
      </SectionTitle>
      <InfoGrid>
        <InfoLabel>
          {tr('pbl.citizenAuthorizationCaseCreate.shared.officeFields.legcoOffice', "LegCo Members' Office")}
        </InfoLabel>
        <InfoValue>{displayValue(form.oc_office_name)}</InfoValue>
        <InfoLabel>
          {tr(
            'pbl.citizenAuthorizationCaseCreate.shared.officeFields.assistingStaffName',
            "Assisting Staff Name (LegCo Members' Office)"
          )}
        </InfoLabel>
        <InfoValue>{displayValue(form.assisting_staff_name)}</InfoValue>
        <InfoLabel>
          {tr('pbl.citizenAuthorizationCaseCreate.shared.officeFields.officeContact', 'Office Contact')}
        </InfoLabel>
        <InfoValue>{displayValue(form.office_contact)}</InfoValue>
      </InfoGrid>

      <SectionTitle>
        {tr('pbl.citizenAuthorizationCaseCreate.shared.sections.enquirerInformation', "Enquirer's Information")}
      </SectionTitle>
      <InfoGrid>
        <InfoLabel>
          {tr(
            'pbl.citizenAuthorizationCaseCreate.shared.enquirerFields.fullNameEnglishChinese',
            'Full Name (English/ Chinese)'
          )}
        </InfoLabel>
        <InfoValue>{displayValue(form.citizen_full_name)}</InfoValue>
        <InfoLabel>
          {tr(
            'pbl.citizenAuthorizationCaseCreate.shared.enquirerFields.identityPartialReadOnly',
            'HKID / Passport No.:(partial first 4 digits only)'
          )}
        </InfoLabel>
        <InfoValue>{displayValue(form.identity_partial)}</InfoValue>
        <InfoLabel>
          {tr('pbl.citizenAuthorizationCaseCreate.shared.enquirerFields.phoneNumberReadOnly', 'Phone Number')}
        </InfoLabel>
        <InfoValue>{displayValue(form.phone)}</InfoValue>
        <InfoLabel>
          {tr('pbl.citizenAuthorizationCaseCreate.shared.enquirerFields.emailAddress', 'Email Address')}
        </InfoLabel>
        <InfoValue>{displayValue(form.email)}</InfoValue>
        <InfoLabel>
          {tr(
            'pbl.citizenAuthorizationCaseCreate.shared.enquirerFields.residentialAddressReadOnly',
            'Residential Address'
          )}
        </InfoLabel>
        <InfoValue>{displayValue(form.residential_address)}</InfoValue>
      </InfoGrid>

      <FieldGroup>
        <SectionTitle>
          {tr('pbl.citizenAuthorizationCaseCreate.caseApplication.enquiryCategory.title', 'Enquiry Category')}
        </SectionTitle>
        <PromptText>
          {tr(
            'pbl.citizenAuthorizationCaseCreate.caseApplication.enquiryCategory.prompt',
            'Select ONLY ONE most relevant category in relation to the enquiry *'
          )}
        </PromptText>
        <OptionStack>
          {enquiryCategories.map((item) => (
            <FormControlLabel
              key={item.value}
              control={
                <Radio
                  checked={form.enquiry_category === item.value}
                  onChange={() => updateField('enquiry_category', item.value)}
                />
              }
              label={<OptionText>{tr(item.labelKey, item.defaultLabel)}</OptionText>}
            />
          ))}
        </OptionStack>
      </FieldGroup>

      <FieldGroup>
        <SectionTitle id="case-urgency-title">
          {tr('pbl.citizenAuthorizationCaseCreate.caseApplication.urgency.title', 'Urgency and court deadline')}
        </SectionTitle>
        <OptionStack role="radiogroup" aria-labelledby="case-urgency-title">
          {(['normal', 'urgent'] as const).map((urgency) => (
            <FormControlLabel
              key={urgency}
              control={<Radio checked={form.urgency === urgency} onChange={() => updateField('urgency', urgency)} />}
              label={tr(
                `pbl.citizenAuthorizationCaseCreate.caseApplication.urgency.${urgency}`,
                urgency === 'normal' ? 'Normal' : 'Urgent'
              )}
            />
          ))}
        </OptionStack>
        <TextField
          label={tr('pbl.citizenAuthorizationCaseCreate.caseApplication.urgency.courtDeadline', 'Court deadline (if any)')}
          type="date"
          variant="standard"
          value={form.court_deadline}
          onChange={(event) => updateField('court_deadline', event.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <PromptText>
          {tr(
            'pbl.citizenAuthorizationCaseCreate.caseApplication.urgency.warning',
            'An urgent enquiry or court deadline does not guarantee that a lawyer can be arranged or that assistance will be available before the deadline.'
          )}
        </PromptText>
      </FieldGroup>

      <FieldGroup>
        <SectionTitle>
          {tr(
            'pbl.citizenAuthorizationCaseCreate.caseApplication.legalIssue.title',
            'Detailed Description of Legal Issue'
          )}
        </SectionTitle>
        <TextFieldStack>
          <TextField
            label={tr(
              'pbl.citizenAuthorizationCaseCreate.caseApplication.legalIssue.factualBackground',
              'Factual Background (recommended 50-500 words, state the incident process, parties involved, key time nodes) *'
            )}
            variant="standard"
            value={form.factual_background}
            onChange={(event) => updateField('factual_background', event.target.value)}
            multiline
            fullWidth
            helperText={tr(
              'pbl.citizenAuthorizationCaseCreate.caseApplication.legalIssue.factualBackgroundRecommendation',
              CITIZEN_CASE_FACTUAL_BACKGROUND_RECOMMENDATION
            )}
          />
          <TextField
            label={tr(
              'pbl.citizenAuthorizationCaseCreate.caseApplication.legalIssue.specificQueries',
              'Specific queries (Clarify the legal assistance the enquirer needs) *'
            )}
            variant="standard"
            value={form.specific_queries}
            onChange={(event) => updateField('specific_queries', event.target.value)}
            multiline
            fullWidth
          />
        </TextFieldStack>
      </FieldGroup>

      <FieldGroup>
        <SectionTitle>
          {tr(
            'pbl.citizenAuthorizationCaseCreate.caseApplication.requirements.title',
            'Enquiry Requirements & Expectations'
          )}
        </SectionTitle>
        <PromptText>
          {tr(
            'pbl.citizenAuthorizationCaseCreate.caseApplication.requirements.primaryLegalServiceNeeds',
            'Primary Legal Service Needs *'
          )}
        </PromptText>
        <OptionStack>
          {pblLegalServiceNeeds.map((item) => (
            <FormControlLabel
              key={item.value}
              control={
                <Checkbox
                  checked={form.primary_legal_service_needs.includes(item.value)}
                  onChange={toggleLegalNeed(item.value)}
                />
              }
              label={<OptionText>{tr(item.labelKey, item.defaultLabel)}</OptionText>}
            />
          ))}
        </OptionStack>
        {form.primary_legal_service_needs.includes('other') && (
          <TextField
            label={tr(
              'pbl.citizenAuthorizationCaseCreate.caseApplication.requirements.otherLegalServiceNeed',
              'Other legal service need'
            )}
            variant="standard"
            value={form.primary_legal_service_needs_other}
            onChange={(event) => updateField('primary_legal_service_needs_other', event.target.value)}
            fullWidth
          />
        )}
      </FieldGroup>

      <FieldGroup>
        <PromptText>
          {tr(
            'pbl.citizenAuthorizationCaseCreate.caseApplication.requirements.preferredConsultationMethod',
            'Preferred Consultation Method (via eBRAM DMP) *'
          )}
        </PromptText>
        <OptionStack>
          {pblConsultationMethods.map((item) => (
            <FormControlLabel
              key={item.value}
              control={
                <Radio
                  checked={form.consultation_methods.includes(item.value)}
                  onChange={() => selectConsultationMethod(item.value)}
                />
              }
              label={<OptionText>{tr(item.labelKey, item.defaultLabel)}</OptionText>}
            />
          ))}
        </OptionStack>
      </FieldGroup>

      <SectionTitle>
        {tr('pbl.citizenAuthorizationCaseCreate.caseApplication.disclaimer.title', 'Disclaimer')}
      </SectionTitle>
      <DisclaimerText>
        {tr(
          'pbl.citizenAuthorizationCaseCreate.caseApplication.disclaimer.body',
          'The provision of legal advice under FreelegalLink (the "Service") is by individual panel solicitors on a pro bono basis. The FreelegalLink Project Team does not accept any responsibilities and shall not be held liable in contract, tort (including negligence), or otherwise for any loss or damage whatsoever arising from and/or suffered in connection with the Service and/or any legal advice provided by panel solicitors.'
        )}
      </DisclaimerText>

      <FieldGroup>
        <SectionTitle>
          {tr('pbl.citizenAuthorizationCaseCreate.caseApplication.supportingDocuments.title', 'Document(s) upload')}
        </SectionTitle>
        <DocumentUploadHelp>
          {tr(
            'pbl.citizenAuthorizationCaseCreate.caseApplication.supportingDocuments.help',
            'Upload any documents that may help the Office review the enquiry before consultation.'
          )}
        </DocumentUploadHelp>
        <DocumentUploadHelp>
          {form.uploaded_documents.length} / {CASE_SUPPORTING_DOCUMENT_MAX_COUNT}
        </DocumentUploadHelp>
        <DropZone files={caseDocumentFiles} onFilesDrop={setCaseDocumentFiles} setFiles={setCaseDocumentFiles} />
        <DocumentUploadHelp>
          {isUploadingCaseDocument
            ? tr('pbl.citizenAuthorizationCaseCreate.caseApplication.supportingDocuments.uploading', 'Uploading...')
            : tr(
                'pbl.citizenAuthorizationCaseCreate.caseApplication.supportingDocuments.uploadedHelp',
                'Uploaded documents will be attached to this enquiry.'
              )}
        </DocumentUploadHelp>
        {form.uploaded_documents.length > 0 ? (
          <UploadedDocumentList>
            {form.uploaded_documents.map((document) => (
              <UploadedDocumentItem key={document.storage_file_id}>
                <UploadedDocumentName>{document.title}</UploadedDocumentName>
                <Button
                  type="button"
                  size="sm"
                  variant="danger-invert"
                  rounded={false}
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      uploaded_documents: current.uploaded_documents.filter(
                        (item) => item.storage_file_id !== document.storage_file_id
                      ),
                    }))
                  }
                >
                  {tr('buttons.remove', 'Remove')}
                </Button>
              </UploadedDocumentItem>
            ))}
          </UploadedDocumentList>
        ) : null}
      </FieldGroup>

      <SectionTitle>{tr('pbl.citizenAuthorizationCaseCreate.shared.sections.signature', 'Signature')}</SectionTitle>
      <SignatureRow isMobile={isMobile}>
        <Text as="span" margin={0}>
          {tr('pbl.citizenAuthorizationCaseCreate.caseApplication.signature.enquirer', 'Signature of Enquirer :')}
        </Text>
        {form.enquirer_signature ? (
          <SignedBadge>{tr('pbl.citizenAuthorizationCaseCreate.shared.actions.signed', 'Signed')}</SignedBadge>
        ) : (
          <Button
            type="button"
            size="md"
            variant="primary"
            rounded={false}
            onClick={() => setIsSignatureModalOpen(true)}
          >
            {tr('pbl.citizenAuthorizationCaseCreate.shared.actions.sign', 'Sign')}
          </Button>
        )}
        <DateText>
          {tr('pbl.citizenAuthorizationCaseCreate.caseApplication.signature.date', 'Date :')}{' '}
          {form.enquirer_signed_at ? formatPblDisplayDate(form.enquirer_signed_at) : ''}
        </DateText>
      </SignatureRow>
      {form.enquirer_signature ? (
        <SignaturePreview>
          <SignaturePreviewImage
            src={form.enquirer_signature}
            alt={tr('pbl.citizenAuthorizationCaseCreate.caseApplication.signature.alt', 'Enquirer signature preview')}
          />
          <SignaturePreviewAction type="button" onClick={() => setIsSignatureModalOpen(true)}>
            {tr('pbl.citizenAuthorizationCaseCreate.shared.actions.resign', 'Re-sign')}
          </SignaturePreviewAction>
        </SignaturePreview>
      ) : null}

      <ActionRow isMobile={isMobile}>
        <Button
          type="button"
          size="lg"
          variant="primary-invert"
          rounded={false}
          disabled={isSavingDraft || isSubmitting}
          onClick={saveDraft}
        >
          {isSavingDraft
            ? tr('pbl.citizenAuthorizationCaseCreate.caseApplication.actions.savingDraft', 'Saving...')
            : tr('pbl.citizenAuthorizationCaseCreate.caseApplication.actions.saveDraft', 'Save Draft')}
        </Button>
        <Button type="submit" size="lg" variant="primary" rounded={false} disabled={!isComplete || isSubmitting}>
          {isSubmitting
            ? tr('pbl.citizenAuthorizationCaseCreate.caseApplication.actions.submitting', 'Submitting...')
            : tr('pbl.citizenAuthorizationCaseCreate.caseApplication.actions.submitEnquiry', 'Submit Enquiry')}
        </Button>
        <Button type="button" size="lg" variant="primary-invert" rounded={false} onClick={() => navigate('/home')}>
          {tr('pbl.citizenAuthorizationCaseCreate.shared.actions.back', 'Back')}
        </Button>
      </ActionRow>
      <SignatureModal
        isOpen={isSignatureModalOpen}
        onClose={closeSignatureModal}
        onConfirm={saveSignature}
        onEmptySignature={() =>
          errorHandler(
            tr(
              'pbl.citizenAuthorizationCaseCreate.shared.messages.signatureRequired',
              'Please provide a signature before confirming.'
            )
          )
        }
        title={tr(
          'pbl.citizenAuthorizationCaseCreate.accountRegistration.signature.enquirerModalTitle',
          'Signature of Enquirer'
        )}
      />
    </FormPanel>
  );
};

export default CitizenCaseApplication;
